import React, {
	useMemo,
	useContext,
	useCallback,
	useEffect,
	useState,
} from "react";
import styled, { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import { Body } from "../../UI/Text";
import Table from "../../UI/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import {
	deleteLine,
	getLines,
} from "../../../requests/quoteManagementRequests";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import DetailRow from "./DetailRow";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import PriceDisplay from "../PriceDisplay";
import UserContext from "../../../context/user/UserContext";
import Button from "../../UI/Button";
import * as constants from "../../../modules/constants";
import _ from "lodash";
import { useQuery } from "../../Hooks";

const ButtonsWrapper = styled(Flex)`
	justify-content: center;
	align-items: center;
	transition: 200ms all ease-out;
	height: 100%;
`;

const LineImg = styled.img`
	max-width: 75px;
	max-height: 75px;
	width: auto !important;
	height: auto;
`;

const LineTable = ({ quoteId, open, isLoading }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const { activeUser, isLoading: userLoading } = useContext(UserContext);
	const { 1: priceMode } = location.pathname.split("/");
	const {
		quote,
		setQuote,
		setIsReconfigure,
		setActiveProduct,
		setConfiguratorMode,
		printing,
	} = useContext(ConfiguratorContext);
	const isPlane = useMemo(() => quote?.isPlane, [quote]);
	const isDealer = useMemo(
		() => priceMode === "DealerQuote" || !isPlane,
		[priceMode, isPlane]
	);
	const isQuote = useMemo(() => quote?.type === 1, [quote]);

	const _lines = useQuery({
		queryID: `lines|${quoteId}`,
		request: getLines,
		queryVariables: { quoteId, isDealer, erpId: quote?.customer?.erpId },
		skip:
			!quoteId || !quote?.customer?.erpId || userLoading || isLoading || !quote,
	});
	useEffect(() => {
		if (
			_lines?.data &&
			!_lines.isLoading &&
			!quote?.lines &&
			!quote?.lineCount &&
			!isLoading &&
			!!quote
		)
			setQuote({
				...quote,
				lines: _lines?.data?.rows,
				lineCount: _lines?.data?.count,
			});
	}, [_lines.data, _lines.isLoading, isLoading, quote, setQuote]);

	const lines = useMemo(() => quote?.lines || [], [quote]);

	const lineCount = useMemo(() => {
		return quote?.lineCount || [];
	}, [quote]);

	const onReconfigure = useCallback(
		async (row) => {
			const {
				productId,
				configurationId,
				id: LineId,
				sourceConfigurationId,
				sourceHeaderId,
				headerId,
			} = row;
			setIsReconfigure(true);
			setActiveProduct(productId);
			setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);

			const lineNumber =
				_.findIndex(quote?.lines, (line) => line.id === LineId) + 1;

			let paramsObj = {
				lineNumber,
				LineId,
			};

			paramsObj.headerId = sourceHeaderId || headerId;

			paramsObj.configurationId = sourceConfigurationId || configurationId;

			navigate({
				pathname: `/Configurator/${productId}/${quoteId}/${configurationId}`,
				search: createSearchParams(paramsObj).toString(),
			});
		},
		[
			setIsReconfigure,
			setActiveProduct,
			setConfiguratorMode,
			quote,

			navigate,
			quoteId,
		]
	);

	const roleLevel = useMemo(() => activeUser?.roleLevel, [activeUser]);
	const activeOrderId = useMemo(() => quote?.activeOrderId, [quote]);

	const onDeleteLine = useCallback(
		async (row) => {
			const { lines } = quote || {};
			const { _id, price = 0, quantity = 1 } = row || {};

			const newLines = [...lines];
			const i = _.findIndex(newLines, (line) => line?._id === _id);
			newLines.splice(i, 1);

			const newPrice = quote?.totalCost - +price * +quantity;

			setQuote({
				...quote,
				totalCost: newPrice,
				lines: newLines,
				lineCount: quote?.lineCount - 1,
			});
			await deleteLine({ _id, quoteId });
		},
		[quote, setQuote, quoteId]
	);

	const columns = useMemo(() => {
		const cols = [
			{
				id: "imgUrl",
				data: (row) => (
					<Flex
						width={1}
						alignItems="center"
						height="100%"
						key={row?.id + "|imgUrl"}
						justifyContent="center"
					>
						{(row?.imgUrl?.startsWith("http") ||
							row?.imgUrl?.startsWith("/") ||
							row?.base64Image) && (
							<LineImg
								src={row?.base64Image ? row.base64Image : row?.imgUrl}
								alt={row?.id || "imgUrl"}
							/>
						)}
					</Flex>
				),
				header: "Preview",
				size: { md: 1.5, sm: -1, xs: -1 },
				sort: false,
			},
			{
				id: "partNumber",
				data: (row) => (
					<Flex
						height="100%"
						justifyContent="center"
						alignItems="center"
						key={row?.id + "|partNumber"}
					>
						<Body color={row.id === "new" ? theme.secondary : undefined} center>
							{row.partNumber}
						</Body>
					</Flex>
				),
				header: "Part #",
				size: { md: 1.5, sm: 1.5, xs: 1.5 },
				sort: false,
			},
			{
				id: "description",
				data: (row) => (
					<Flex
						height="100%"
						justifyContent="center"
						alignItems="center"
						flexWrap="wrap"
						key={row?.id + "|description"}
					>
						<Body>{row.description}</Body>
					</Flex>
				),
				header: "Description",
				size: { md: 4, sm: 3, xs: 0 },
				sort: false,
			},
			{
				id: "quantity",
				data: (row) => {
					return row.id === "new" ? null : <Body center>{row?.quantity}</Body>;
				},
				header: "Qty.",
				size: { md: 1, sm: 1, xs: 1 },
				sort: false,
			},
			{
				id: "price",
				data: (row) => (
					<PriceDisplay
						isDealer={isDealer}
						isPlane={isPlane}
						isUnit
						row={row}
					/>
				),
				header: "Unit Price",
				size: { md: 2.5, sm: -1, xs: -1 },
				sort: false,
			},
			{
				id: "totalPrice",
				data: (row) => (
					<PriceDisplay isDealer={isDealer} isPlane={isPlane} row={row} />
				),
				header: "Total",
				size: { md: 2.5, sm: 2, xs: 2 },
				sort: false,
			},
			{
				id: "actions",
				header: "",
				sort: false,
				data: (row) => {
					return (
						<ButtonsWrapper
							id="actions"
							className="no-print"
							m="0 auto"
							height="100%"
							key={row?.id + "|actions"}
						>
							{/* {quote?.type === 2 ? (
                <Button
                  size="small"
                  reverse
                  disabled={!row?.TrackingURL}
                  color={theme.success}
                  title="Track Shipment"
                  iconName="faTruck"
                  onClick={() => navigate(row?.TrackingURL)}
                />
              ) : null} */}
							{!row.configurationId ? (
								<Button
									size="small"
									color={theme.danger}
									reverse
									title="Delete Line"
									iconName="faTrashCan"
									onClick={() => onDeleteLine(row)}
								/>
							) : (
								<>
									{isQuote ? (
										<>
											<Button
												size="small"
												ml="1rem"
												color={theme.secondary}
												reverse
												title="Reconfigure Line"
												iconName="faPencil"
												onClick={() => onReconfigure(row)}
											/>
											<Button
												size="small"
												color={theme.danger}
												title="Delete Line"
												reverse
												iconName="faTrashCan"
												onClick={() => onDeleteLine(row)}
											/>
										</>
									) : null}
								</>
							)}
						</ButtonsWrapper>
					);
				},
				size:
					roleLevel >= 5 && !!activeOrderId && !printing
						? { lg: 4, md: 0, sm: 0, xs: 0 }
						: { md: -1, sm: -1, xs: -1 },
			},
		];
		//{
		/* <Button
                    size="small"
                    color={theme.secondary}
                    reverse
                    iconName="faCopy"
                    onClick={() => onCopyLine(row)}
                  /> */
		//	}
		if (!!isPlane)
			cols.push({
				id: "Details",
				header: "",
				data: (row) => {
					const { Details: _Details } = row || {};

					console.log({ _Details });

					if (!_Details) return null;

					let Details = _Details;
					if (typeof _Details === "string" || _Details instanceof String)
						Details = JSON.parse(_Details);

					//= JSON.parse(_Details) || []; // JSON.parse(_Details);
					return (
						<Flex mb="1rem" width={1} flexDirection="column">
							<Flex
								m="0 -1rem 1rem -1rem"
								key="divider"
								width="calc(2rem + 100%)"
								style={{
									borderBottom: `2px solid ${theme.borderColor}`,
								}}
							/>
							{Details.map((Detail, i) => (
								<DetailRow {...{ Detail, i }} />
							))}
						</Flex>
					);
				},
				sort: false,
				size: { xs: 0 },
			});

		return cols;
	}, [
		roleLevel,
		activeOrderId,
		printing,
		isPlane,
		theme.secondary,
		theme.danger,
		theme.borderColor,
		isDealer,
		isQuote,
		onDeleteLine,
		onReconfigure,
	]);

	return (
		<Table
			hideBack
			hideCreate
			hideSearch
			open={open}
			hideProperties
			hidePager
			isLoading={_lines.isLoading || !_lines.data}
			data={lines || []}
			columns={columns}
			totalCount={lineCount}
		/>
	);
};

export default LineTable;
